import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/offlineConnection.css';

function OfflineIllustration() {
  return (
    <div className="offline-illustration">
      <svg
        className="offline-svg"
        viewBox="0 0 520 300"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <filter id="offlineShadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow
              dx="0"
              dy="14"
              stdDeviation="14"
              floodColor="#7D96C8"
              floodOpacity="0.18"
            />
          </filter>
          <linearGradient id="offlineServerGradient" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#F4F8FF" />
            <stop offset="1" stopColor="#DCE7FA" />
          </linearGradient>
          <linearGradient id="offlineRedGradient" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#FF9696" />
            <stop offset="1" stopColor="#FF5F67" />
          </linearGradient>
        </defs>
        <circle cx="226" cy="74" r="50" stroke="#AFCBFF" strokeWidth="2" strokeDasharray="9 10" />
        <g filter="url(#offlineShadow)">
          <rect x="104" y="92" width="186" height="58" rx="17" fill="url(#offlineServerGradient)" />
          <rect x="104" y="145" width="186" height="58" rx="17" fill="url(#offlineServerGradient)" />
          <rect x="104" y="198" width="186" height="58" rx="17" fill="url(#offlineServerGradient)" />
          <circle cx="132" cy="120" r="6" fill="#4386F5" />
          <circle cx="151" cy="120" r="6" fill="#85AEF6" />
          <circle cx="132" cy="173" r="6" fill="#4386F5" />
          <circle cx="151" cy="173" r="6" fill="#85AEF6" />
          <rect x="127" y="221" width="36" height="10" rx="5" fill="#5A96F5" />
        </g>
        <g filter="url(#offlineShadow)">
          <path
            d="M249 218 H386 C425 218 452 195 452 163 C452 132 429 109 399 107 C389 72 358 47 320 47 C278 47 244 78 238 118 C211 119 189 141 189 168 C189 196 214 218 249 218Z"
            fill="white"
          />
        </g>
        <path d="M435 85L448 62" stroke="#9CB8EC" strokeWidth="6" strokeLinecap="round" />
        <path d="M454 99L476 85" stroke="#9CB8EC" strokeWidth="6" strokeLinecap="round" />
        <g filter="url(#offlineShadow)">
          <circle cx="360" cy="207" r="62" fill="#FFE8EA" />
          <circle
            cx="360"
            cy="207"
            r="53"
            fill="white"
            stroke="url(#offlineRedGradient)"
            strokeWidth="9"
          />
          <path
            d="M326 190C346 170 375 170 395 190"
            stroke="#C6424B"
            strokeWidth="8"
            strokeLinecap="round"
          />
          <path
            d="M340 205C352 193 369 193 381 205"
            stroke="#C6424B"
            strokeWidth="8"
            strokeLinecap="round"
          />
          <circle cx="360" cy="222" r="6" fill="#C6424B" />
          <path d="M322 166L400 244" stroke="#FF727A" strokeWidth="11" strokeLinecap="round" />
        </g>
      </svg>
    </div>
  );
}

async function probeOnline() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 3500);
    const response = await fetch(`/manifest.json?offline-check=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    window.clearTimeout(timer);
    return response.ok;
  } catch {
    return false;
  }
}

export default function OfflineConnectionOverlay() {
  const [offline, setOffline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine === false : false
  );
  const [tipsOpen, setTipsOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const syncOnlineState = useCallback(async () => {
    const online = await probeOnline();
    setOffline(!online);
    return online;
  }, []);

  useEffect(() => {
    const onOffline = () => setOffline(true);
    const onOnline = () => {
      syncOnlineState();
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    syncOnlineState();
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [syncOnlineState]);

  useEffect(() => {
    if (!offline) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [offline]);

  const handleRetry = async () => {
    setRetrying(true);
    const online = await syncOnlineState();
    setRetrying(false);
    if (online) {
      window.location.reload();
    }
  };

  if (!offline || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="offline-overlay-root" role="alertdialog" aria-modal="true" aria-labelledby="offline-title">
      <div className="offline-page">
        <main className="offline-content">
          <OfflineIllustration />

          <div className="offline-status">
            <span className="offline-status-dot" />
            Сервер недоступен
            <span className="offline-status-separator">•</span>
            offline
          </div>

          <h1 className="offline-title" id="offline-title">
            Нет связи с сервером
          </h1>

          <p className="offline-description">
            Похоже, устройство не подключено к интернету.
            <br />
            Проверьте соединение и попробуйте снова.
          </p>

          <div className="offline-actions">
            <button
              className="offline-button offline-button-primary"
              type="button"
              onClick={handleRetry}
              disabled={retrying}
            >
              <svg viewBox="0 0 24 24" width="21" height="21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M20 6V11H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4.5 9A8 8 0 0 1 18.6 5.8L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M4 18V13H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19.5 15A8 8 0 0 1 5.4 18.2L4 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {retrying ? 'Проверяем…' : 'Повторить'}
            </button>

            <button
              className="offline-button offline-button-secondary"
              type="button"
              onClick={() => setTipsOpen(true)}
            >
              <svg viewBox="0 0 24 24" width="21" height="21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M12 15.5A3.5 3.5 0 1 0 12 8.5A3.5 3.5 0 0 0 12 15.5Z" stroke="currentColor" strokeWidth="1.8" />
                <path
                  d="M19.4 15A1.7 1.7 0 0 0 19.74 16.88L19.8 16.94A2 2 0 1 1 16.97 19.77L16.91 19.71A1.7 1.7 0 0 0 15.03 19.37A1.7 1.7 0 0 0 14 20.93V21A2 2 0 1 1 10 21V20.91A1.7 1.7 0 0 0 8.9 19.36A1.7 1.7 0 0 0 7.02 19.7L6.96 19.76A2 2 0 1 1 4.13 16.93L4.19 16.87A1.7 1.7 0 0 0 4.53 14.99A1.7 1.7 0 0 0 3 14H2.9A2 2 0 1 1 2.9 10H3A1.7 1.7 0 0 0 4.54 8.97A1.7 1.7 0 0 0 4.2 7.09L4.14 7.03A2 2 0 1 1 6.97 4.2L7.03 4.26A1.7 1.7 0 0 0 8.91 4.6H9A1.7 1.7 0 0 0 10 3.08V3A2 2 0 1 1 14 3V3.1A1.7 1.7 0 0 0 15.03 4.63A1.7 1.7 0 0 0 16.91 4.29L16.97 4.23A2 2 0 1 1 19.8 7.06L19.74 7.12A1.7 1.7 0 0 0 19.4 9V9.08A1.7 1.7 0 0 0 20.93 10H21A2 2 0 1 1 21 14H20.9A1.7 1.7 0 0 0 19.4 15Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              Проверить настройки
            </button>
          </div>

          <button
            className="offline-details"
            type="button"
            aria-expanded={tipsOpen}
            aria-controls="offline-tips-panel"
            onClick={() => setTipsOpen((value) => !value)}
          >
            <span>{tipsOpen ? 'Скрыть' : 'Подробнее'}</span>
            <svg
              className="offline-details-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div
            className={`offline-tips${tipsOpen ? ' is-open' : ''}`}
            id="offline-tips-panel"
          >
            <article className="offline-tip">
              <div className="offline-tip-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 10.5C9.1 6.9 14.9 6.9 19 10.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M8 14C10.3 12 13.7 12 16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="18" r="1.6" fill="currentColor" />
                </svg>
              </div>
              <div>
                <h3>Проверьте Wi-Fi</h3>
                <p>Убедитесь, что устройство подключено к сети.</p>
              </div>
            </article>

            <article className="offline-tip">
              <div className="offline-tip-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 18V15" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  <path d="M9.5 18V12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  <path d="M14 18V8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  <path d="M18.5 18V4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </div>
              <div>
                <h3>Проверьте мобильный интернет</h3>
                <p>Если используете мобильную сеть, убедитесь в наличии сигнала.</p>
              </div>
            </article>

            <article className="offline-tip">
              <div className="offline-tip-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
                  <path d="M4.5 12H19.5" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 4C14 6.1 15 8.8 15 12C15 15.2 14 17.9 12 20" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 4C10 6.1 9 8.8 9 12C9 15.2 10 17.9 12 20" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <div>
                <h3>Попробуйте позже</h3>
                <p>Если проблема сохраняется, возможно, ведутся технические работы.</p>
              </div>
            </article>
          </div>
        </main>
      </div>
    </div>,
    document.body
  );
}
